/*
 * @author Andrey Patyuchenko
 * @email  apatyuchenko@atlassian.com
 * @email  andrey.patyuchenko@gmail.com
 * @date   3/11/22
 */

import React, {useEffect, useMemo} from "react";
import {Box} from "@mui/material";
import Header from "./Header";
import Body from "./Body";
import {
	addDays,
	eachHourOfInterval,
	addSeconds,
	areIntervalsOverlapping,
	isToday,
	isWeekend,
	differenceInHours, setMinutes, setHours,
} from "date-fns";
import { VegaEvent, Employee, GoogleEvent, Holiday, WorkdayEvent, OpsGenieEvent } from "../interfaces/Global";
import zonedTimeToUtc from "date-fns-tz/zonedTimeToUtc";
import {useSelector} from "react-redux";
import {State} from "../state/store";

export interface DayCalendarProps {
	day: Date
	employees: Employee[]
	vegaEvents: VegaEvent[]
	googleEvents: GoogleEvent[]
	workdayEvents: WorkdayEvent[]
	opsGenieEvents: OpsGenieEvent[]
	holidays: Holiday[]
}

const getShiftForEmployee = (employee: Employee, day: Date): Interval => {
	const startTime = employee.startTime || "9:00";
	const startHour = Number(startTime.split(":")[0]);
	const startMin = Number(startTime.split(":")[1]);
	const endTime = employee.endTime || "18:00";
	const endHour = Number(endTime.split(":")[0]);
	const endMin = Number(endTime.split(":")[1]);
	const tz = employee.timeZone || "UTC";
	let startDt = zonedTimeToUtc(setMinutes(setHours(day, startHour), startMin), tz);
	let endDt = zonedTimeToUtc(setMinutes(setHours(day, endHour), endMin), tz);
	return {start: startDt, end: endDt}
}

const getOutOfOfficeEvents = (employees: Employee[], day: Date) => {
	let oooEvents: Record<string, Interval[]> = {};
	const dayInterval: Interval = {start: day, end: addDays(day, 1)};
	employees.forEach(employee => {
		const tz = employee.timeZone;
		if (tz) {
			const shift = getShiftForEmployee(employee, day);
			let events = [-5,-4,-3,-2,-1,0,1,2,3,4,5].map(x => [addDays(shift.start, x), addDays(shift.end, x)])
				.filter(e => !isWeekend(e[0]));
			let employeeOooEvents: Interval[] = [];
			events.forEach((s,i) => {
				if (i !== events.length-1) {
					employeeOooEvents.push({start: s[1], end: events[i+1][0]})
				}
			});
			oooEvents[employee.id] = employeeOooEvents.filter(s => areIntervalsOverlapping(s, dayInterval))
		}
	});
	return oooEvents;
}

export default function DayCalendar({ employees, vegaEvents, googleEvents, workdayEvents, opsGenieEvents, day, holidays }: DayCalendarProps) {

	const displayOooEvents = useSelector((s:State) => s.settings.displayOooEvents);
	const range = useMemo(() => ({start: day, end: addSeconds(addDays(day, 1), -1)}), [day]);
	const times = useMemo(() => eachHourOfInterval(range), [range]);
	const oooEvents = useMemo(() => displayOooEvents ? getOutOfOfficeEvents(employees, day) : {}, [day, employees, displayOooEvents]);

	useEffect(() => {
		if (isToday(day)) {
			let left = differenceInHours(new Date(), day)*150-150;
			const calendar = document.getElementById('day-calendar');
			if (calendar && left > 0 && left < 3600) {
				calendar.scrollTo({left: left});
			}
		}
	}, [day]);

	return (
		<Box m={1} sx={{ position: "relative", overflowX: "auto" }} id={'day-calendar'}>
			<Header
				intervals={times}
				formats={['p']}
				intervalWidth={150}
				rangeResolution={3600/1440}
			/>
			<Body
				range={{ start: day, end: addDays(day, 1) }}
				intervals={times}
				intervalWidth={150}
				intervalDuration={60}
				rangeResolution={3600/1440}
				employees={employees||[]}
				vegaEvents={(vegaEvents||[]).filter(e => areIntervalsOverlapping(
					{start: e.startDatetime, end: e.endDatetime},
					range
				))}
				googleEvents={googleEvents.filter(e =>
					employees.some(em => em.id === e.employeeId) &&
					areIntervalsOverlapping({ start: e.startDatetime, end: e.endDatetime }, range)
				)}
				workdayEvents={workdayEvents.filter(e =>
					areIntervalsOverlapping({ start: e.startDatetime, end: e.endDatetime }, range)
				)}
				opsGenieEvents={opsGenieEvents.filter(e =>
					areIntervalsOverlapping({ start: e.startDatetime, end: e.endDatetime }, range)
				)}
				oooEvents={oooEvents}
				holidays={holidays.filter(h =>
					employees.some(em => em.countryCode === h.countryCode) &&
					areIntervalsOverlapping({ start: h.startDate, end: h.endDate }, range)
				)}
				normalizeEventsPosition={false}
				capacityCalculateResolution={5}
			/>
		</Box>
	);

}